// VehTypeInfoInput.js
import React, { useEffect, useState } from 'react';
import { Button, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import InputUtil from '../../value/InputUtil';
import VzmTextField from '../../common/VzmTextField';
import VzmNumberField from '../../common/VzmNumberField';
import VzmCheckbox from '../../common/VzmCheckbox';
import VzmSelector from '../../common/VzmSelector';
import { VzmInputBlock } from '../../common/VzmStyled';
import { VehTypeInfoDictionary } from './VehTypeInfoDict';
import { useAlertSetter } from '../../api/app-hook';

const InputMap = VehTypeInfoDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * 차종 정보 입력 화면
 */
export default function VehTypeInfoInput({
    open,
    item,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;
            }
            else {
                inputSet.data = {...defaultInputData};
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item]);

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        if((""+param.minTemp).length > 0 && (""+param.maxTemp).length > 0) {
            if(param.minTemp >= param.maxTemp) {
                addAlert({title: '온도 범위 오류', message: ['최소 온도가 최대 온도보다 같거나 높습니다.']});
                return;
            }
        }
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    return(
        <DraggableEditor open={open} maxWidth='md'
            title={isEdit ? '차종 정보 수정' : '차종 정보 입력'}
        >
            <DialogContent>
                <VzmInputBlock style={{minWidth:500}}>
                    <VzmTextField
                        dict={InputMap.vehTypeName}
                        value={input.data.vehTypeName}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmCheckbox
                        dict={InputMap.freshCargo}
                        value={input.data.freshCargo}
                        onChange={onChangeValue}
                    />
                    {
                        input.data.freshCargo
                        ?
                        <>
                            <VzmNumberField
                                dict={InputMap.minTemp}
                                value={input.data.minTemp}
                                onChange={onChangeValue}
                            />
                            ~
                            <VzmNumberField
                                dict={InputMap.maxTemp}
                                value={input.data.maxTemp}
                                onChange={onChangeValue}
                            />
                        </>
                        : null
                    }
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmTextField
                        dict={InputMap.cargoUnit}
                        value={input.data.cargoUnit}
                        onChange={onChangeValue}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmNumberField
                        dict={InputMap.loadCapa}
                        value={input.data.loadCapa}
                        onChange={onChangeValue}
                    /> &nbsp;
                    <VzmTextField
                        dict={InputMap.loadCapaText}
                        value={input.data.loadCapaText}
                        onChange={onChangeValue}
                    /> &nbsp;
                    <VzmNumberField
                        dict={InputMap.minLoad}
                        value={input.data.minLoad}
                        onChange={onChangeValue}
                    />
                </VzmInputBlock>
                <VzmInputBlock>
                    <VzmSelector
                        dict={InputMap.rentType}
                        value={input.data.rentType}
                        onChange={onChangeValue}
                    />
                </VzmInputBlock>
            </DialogContent>
            <DialogActions>
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}